import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./Configs/AuthConfigs";
import { AuthService } from './Services/AuthService';
import { GraphService } from './Services/GraphService';
import { AdminPortalHeader } from './AdminPortalHeader';
import { NavigationBar } from './NavigationBar';
import { PendingRequestsPage } from './Pages/AccessRequests/PendingRequestsPage';
import { ApprovedRequestsPage } from './Pages/AccessRequests/ApprovedRequestsPage';
import { RejectedRequestsPage } from './Pages/AccessRequests/RejectedRequestsPage';

import { OrgManagementAppOwnerPage } from './Pages/OrgManagement/OrgManagementAppOwnerPage';
import { UserManagementHomePage } from './Pages/UserManagement/UserManagementHomePage';
import { CreateUserProfilePage } from './Pages/UserManagement/CreateUserProfilePage';
import { CoherenceTheme } from "@coherence-design-system/styles";
import { ThemeProvider, loadTheme } from "@fluentui/react";
import { _Styles } from './Pages/Styles';
import { IUserProfile } from './DataModels/IUserProfile';
import { IAMService } from './Services/IAMService';
import { siteName } from './Constants/SiteConstants';
import { NotificationListItem } from '@coherence-design-system/controls';
import { AppInsightsLogger } from './Utilities/AppInsightsLogger';
import { appInsightsKey } from './Configs/AppInsightsConfigs';
import config from "./Configs/EnvConfigs";

export default function App() {
    const msalContext = useMsal();
    const isSignedIn = useIsAuthenticated();
    
   

    const appInsightsLogger = new AppInsightsLogger(appInsightsKey);
    const authService = new AuthService(msalContext, appInsightsLogger);
    const graphService = new GraphService(authService, appInsightsLogger);
    const iamService = new IAMService(authService, appInsightsLogger);

    const [isNavCollapsed, setNavStatus] = useState(false);
    const [userProfile, setUserProfile] = useState<IUserProfile>();
    const [userProfilePhotoLink, setUserProfilePhotoLink] = useState<string >();
    const [notificationItems, setNotificationItems] = useState<NotificationListItem[]>([]);

    useEffect(() => {
        // Load coherence theme
        loadTheme(CoherenceTheme);
        document.title = siteName;
    }, []);

    useEffect(() => {
        if (isSignedIn) {
            if (!userProfile) {
                graphService.fetchUserProfile().then(setUserProfile);
            }
            if (!userProfilePhotoLink) {
                graphService.fetchUserImage().then(setUserProfilePhotoLink);
            }
        }
    }, [isSignedIn]); // Remove `userProfile`, `userProfilePhotoLink` from dependencies

    useEffect(() => {
        if (!isSignedIn && msalContext.inProgress === InteractionStatus.None) {
            console.log("User not signed in. Triggering login...");
            _handleSignIn();
        }
    }, [isSignedIn, msalContext.inProgress]); // Add `msalContext.inProgress` to dependencies

    const _onNavCollapsed = (isCollapsed: boolean) => {
        setNavStatus(isCollapsed);
    };

    const _handleSignIn = () => {
        if (msalContext.inProgress === InteractionStatus.None) {
            msalContext.instance.loginRedirect(loginRequest).catch(console.error);
        }
    };

    const _handleSignOut = () => {
        msalContext.instance.logoutRedirect();
    };

    const _addRequestAccessNotification = (newNotification: NotificationListItem) => {
        setNotificationItems((prev) => [...prev, newNotification]);
    };

    return (
        <ThemeProvider theme={CoherenceTheme}>
            {isSignedIn ? (
                <AuthenticatedTemplate>
                    <AdminPortalHeader
                        userProfile={userProfile}
                        userProfilePhotoLink={userProfilePhotoLink}
                        logoutAction={_handleSignOut}
                        notificationItems={notificationItems}
                    />
                    <BrowserRouter>
                        <NavigationBar onNavCollapsed={_onNavCollapsed} />
                        <main
                            id="main"
                            role="main"
                            aria-label="Main Content Area"
                            tabIndex={-1}
                            className={isNavCollapsed ? _Styles.mainCollapsed : _Styles.mainExpand}
                        >
                            <Routes>
                                <Route path="/" element={<PendingRequestsPage iamService={iamService} addNotificationAction={_addRequestAccessNotification} logger={appInsightsLogger} />} />
                                <Route path="requests/pending" element={<PendingRequestsPage iamService={iamService} addNotificationAction={_addRequestAccessNotification} logger={appInsightsLogger} />} />
                                <Route path="requests/approved" element={<ApprovedRequestsPage />} />
                                <Route path="requests/rejected" element={<RejectedRequestsPage />} />
                                <Route path="organizations" element={<OrgManagementAppOwnerPage iamService={iamService} />} />
                                <Route path="users" element={<UserManagementHomePage iamService={iamService} />} />
                                <Route path="users/onboard" element={<CreateUserProfilePage iamService={iamService} logger={appInsightsLogger} />} />
                            </Routes>
                        </main>
                    </BrowserRouter>
                </AuthenticatedTemplate>
            ) : (
                <UnauthenticatedTemplate>
                    <div>Redirecting to sign in...</div>
                </UnauthenticatedTemplate>
            )}
        </ThemeProvider>
    );
}
